import React, { useEffect, useState } from 'react';
import { Box, Checkbox, InputLabel, Text } from '@hopskipdrive/react-components';

import { employeesByGroups, groupNameMapping } from '../../utils/helpers/util';
import { StatusChip } from '../shared';

import { CSOption, GroupContainer } from './SRSMultiSelect.styles';

interface SRSMultiSelectProps {
  employees: Employee[];
  handleChange: (ids: string[]) => void;
  handlers: string[] | undefined;
  srsSupervisor?: boolean;
  customSrsSupervisorProps?: {
    updateStatus: (employeeId, status) => void;
    isLoading: boolean;
  };
}

export const SRSMultiSelect: React.FC<SRSMultiSelectProps> = ({
  employees,
  handleChange,
  handlers,
  customSrsSupervisorProps,

  srsSupervisor = false,
}) => {
  const [selectedIds, setSelectedIds] = useState(new Set<string>());
  const groupedEmployees = employeesByGroups(employees);
  const groupDisplayOrder = [
    groupNameMapping.great_start_alert,
    groupNameMapping.p3_alert,
    groupNameMapping.lateness_alert,
    groupNameMapping.ride_ops_alert,
    groupNameMapping.unclaimed_prio_alert,
    groupNameMapping.specialty_transportation,
    groupNameMapping.srs,
  ];

  const allGroupNames = Object.keys(groupedEmployees);

  const sortedGroupNames = allGroupNames.sort((a, b) => {
    // Sort by predefined order
    const indexA = groupDisplayOrder.indexOf(a);
    const indexB = groupDisplayOrder.indexOf(b);

    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    }
    if (indexA !== -1) {
      return -1;
    }
    if (indexB !== -1) {
      return 1;
    }

    return a.localeCompare(b);
  });

  const handleFilterChange = (employeeId: string) => {
    setSelectedIds((prevSelected) => {
      const newSelected = new Set(prevSelected);

      if (newSelected.has(employeeId)) {
        newSelected.delete(employeeId);
      } else {
        newSelected.add(employeeId);
      }

      handleChange([...newSelected]);

      return newSelected;
    });
  };

  const handleGroupToggle = (employeeIdsInGroup: string[], shouldSelectAll: boolean) => {
    setSelectedIds((prevSelected) => {
      const newSelected = new Set(prevSelected);

      if (shouldSelectAll) {
        employeeIdsInGroup.forEach((id) => newSelected.add(id));
      } else {
        employeeIdsInGroup.forEach((id) => newSelected.delete(id));
      }

      handleChange([...newSelected]);

      return newSelected;
    });
  };

  useEffect(() => {
    if (handlers !== undefined) {
      setSelectedIds(new Set(handlers));
    }
  }, [handlers]);

  if (employees.length === 0) {
    return null;
  }

  return (
    <>
      {sortedGroupNames.map((groupName) => {
        const employeeIdsInGroup = groupedEmployees[groupName].map((employee: Employee) => employee.id.toString());
        const selectedInGroup = employeeIdsInGroup.filter((id) => selectedIds.has(id));
        const isAllSelected = selectedInGroup.length === employeeIdsInGroup.length && employeeIdsInGroup.length > 0;
        const isSomeSelected = selectedInGroup.length > 0 && !isAllSelected;

        return (
          groupedEmployees[groupName].length > 0 && (
            <GroupContainer key={groupName}>
              <CSOption>
                <div className="employee-selection">
                  <Checkbox
                    indeterminate={isSomeSelected}
                    checked={isAllSelected}
                    onClick={() => handleGroupToggle(employeeIdsInGroup, !isAllSelected)}
                  />
                  <InputLabel className="option-label">
                    <Text variant="h4" textColor="secondaryText">
                      {groupName}
                    </Text>
                  </InputLabel>
                </div>
              </CSOption>
              <Box data-testid={`cs-handler-filter-${groupName}`} mt={2} overflow="auto" width="195px" ml="15px">
                <ul>
                  {groupedEmployees[groupName].map((employee: Employee) => {
                    return (
                      <li className="option" key={employee.id}>
                        <CSOption>
                          <div className="employee-selection">
                            <Checkbox
                              id={employee.id.toString()}
                              checked={selectedIds.has(employee.id.toString())}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleFilterChange(employee.id.toString());
                              }}
                            />

                            <InputLabel className="option-label" htmlFor={employee.id.toString()}>
                              {employee.displayName}
                            </InputLabel>
                          </div>

                          {srsSupervisor && customSrsSupervisorProps && (
                            <StatusChip
                              employeeId={employee.id}
                              employeeName={employee.displayName}
                              status={employee?.srsAvailability ? employee.srsAvailability.status : 'error'}
                              currentUser={false}
                              updateStatus={customSrsSupervisorProps.updateStatus}
                              isLoading={customSrsSupervisorProps.isLoading}
                            />
                          )}
                        </CSOption>
                      </li>
                    );
                  })}
                </ul>
              </Box>
            </GroupContainer>
          )
        );
      })}
    </>
  );
};
